import type { PropsWithChildren } from 'react'
import type { GridColumn } from '../column/column.types'
import type { GridCellKey, GridCell } from './cell.types'

import { createContext, useContext, useMemo } from 'react'
import { proxy, useSnapshot } from 'valtio'
import { computed } from 'valtio-reactive'

import {
  canUserEditColumn,
  getIsPrivateNotesColumn,
} from '../column/column.helpers'

import { useCueState } from '../cue/CueContext'
import { gridState, gridStateActions } from '../grid/grid.store'
import { isMouseEvent } from '@/utils/events'
import { captureCellSelectionRect } from './content/tiptap/tiptap-focus'

type CellState = {
  cellKey: GridCellKey
  col: GridColumn
  cell: GridCell
}

type DerivedCellState = {
  isReadOnly: boolean
  isInPrivateNotesColumn: boolean
  isSelected: boolean
  isEditing: boolean
}

export const CellContext = createContext<{
  state: CellState
  derivedState: DerivedCellState
} | null>(null)

type ProviderProps = PropsWithChildren & Omit<CellState, 'editorState'>

export const CellContextProvider = ({
  cellKey,
  col,
  cell,
  children,
}: ProviderProps) => {
  const { cue } = useCueState()

  const state: CellState = proxy({
    cellKey,
    col,
    cell,
  })

  const derivedState: DerivedCellState = computed({
    isReadOnly: () => {
      if (getIsPrivateNotesColumn(col)) return false
      if (cue.settings.preventEdits) return true
      return !canUserEditColumn(col)
    },
    isInPrivateNotesColumn: () => {
      // Perf: Skip check for non-text columns
      if (col.type !== 'richtext') return false
      return getIsPrivateNotesColumn(col)
    },
    isSelected: () => gridState.selectedCell === cellKey,
    isEditing: () => gridState.editingCell === cellKey,
  })

  const value = useMemo(() => ({ state, derivedState }), [derivedState, state])
  return <CellContext.Provider value={value}>{children}</CellContext.Provider>
}

export function useCellContext() {
  const context = useContext(CellContext)

  if (!context) {
    throw new Error('Cell context must be used inside a CellContextProvider')
  }

  return context
}

export function useCellState() {
  const context = useCellContext()

  return useSnapshot(context.state)
}

export function useCellDerivedState() {
  const context = useCellContext()

  return useSnapshot(context.derivedState)
}

export function useCellStateActions() {
  const { state } = useCellContext()
  const { cue } = useCueState()

  const actions = {
    setIsSelected: (value: boolean) => {
      if (value) {
        gridStateActions.setSelectedCell(state.cellKey)
      } else {
        gridStateActions.resetSelectedCell()
      }
    },
    setIsEditing: (value: boolean, event?: React.BaseSyntheticEvent) => {
      // `setIsEditing(true)`
      if (value) {
        // If event is mouse-related, set data for focus feature
        const clickCoords =
          event && isMouseEvent(event) && event.type === 'dblclick'
            ? {
                x: event.clientX,
                y: event.clientY,
                selectionRect: captureCellSelectionRect(),
              }
            : null

        gridStateActions.setEditingCell(
          // Key
          state.cellKey,
          // Context
          {
            cellKey: state.cellKey,
            cell: state.cell,
            columnId: state.col.id,
            cueId: cue.id,
            clickCoords: clickCoords,
          },
        )
        // `setIsEditing(false)`
      } else {
        gridStateActions.editor.discard()
      }
    },
  }

  return actions
}
